import { getDocs, query, where, writeBatch } from "firebase/firestore";

import { FirestoreRefs } from "igfd-commons/src/firebase/refs";

import type {
  FirestoreRestaurantSupplierDocument,
  FirestoreSupplierDocument,
  Supplier,
} from "@dashboard/types/supplier";

import {
  readRestaurantSupplier,
  readSupplier,
} from "./firestore-converter/supplier";

export interface SupplierApi {
  fetchSuppliers(restaurantUuid?: string): Promise<Supplier[]>;
  bulkDeleteRestaurantSuppliers(
    restaurantUuid: string,
    supplierIds: string[]
  ): Promise<void>;
}

/**
 * An implementation of the SupplierApi that uses Firestore as the backend.
 * This API handles both global/central suppliers and restaurant-specific suppliers.
 * Note: Suppliers are read-only from the application and managed via kitchen, or integration.
 */
export class FirestoreBackedSupplierApi implements SupplierApi {
  private firestoreRefs: FirestoreRefs;

  constructor(params: { firestoreRefs: FirestoreRefs }) {
    this.firestoreRefs = params.firestoreRefs;
  }

  private async fetchCentralSuppliers(): Promise<Supplier[]> {
    const q = query(
      this.firestoreRefs.suppliers(),
      where("isDeleted", "==", false)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs
      .map((doc) => doc.data() as FirestoreSupplierDocument)
      .map(readSupplier);
  }

  private async fetchRestaurantSuppliers(
    restaurantUuid?: string
  ): Promise<Supplier[]> {
    if (!restaurantUuid) {
      return [];
    }

    const q = query(
      this.firestoreRefs.restaurantSuppliers(restaurantUuid),
      where("isDeleted", "==", false)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs
      .map((doc) => doc.data() as FirestoreRestaurantSupplierDocument)
      .map(readRestaurantSupplier);
  }

  /**
   * Fetches suppliers
   */
  public async fetchSuppliers(restaurantUuid?: string): Promise<Supplier[]> {
    return Promise.all([
      this.fetchCentralSuppliers(),
      this.fetchRestaurantSuppliers(restaurantUuid),
    ]).then(([centralSuppliers, restaurantSuppliers]) => [
      ...centralSuppliers,
      ...restaurantSuppliers,
    ]);
  }

  /**
   * Bulk deletes restaurant suppliers
   * Note: Only restaurant suppliers can be deleted, not central suppliers
   */
  public async bulkDeleteRestaurantSuppliers(
    restaurantUuid: string,
    supplierIds: string[]
  ): Promise<void> {
    // Firestore has a limit of 500 operations per batch
    const BATCH_SIZE = 500;

    if (supplierIds.length === 0) {
      return Promise.resolve();
    }

    const db = this.firestoreRefs.firestore;
    const deletePromises: Promise<void>[] = [];

    for (let i = 0; i < supplierIds.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const chunk = supplierIds.slice(i, i + BATCH_SIZE);

      chunk.forEach((supplierId) => {
        const docRef = this.firestoreRefs.restaurantSupplier(
          restaurantUuid,
          supplierId
        );
        batch.delete(docRef);
      });

      deletePromises.push(batch.commit());
    }

    return Promise.all(deletePromises).then(() => {});
  }
}
